import { AnalysisStatus } from "@/types/scribe";

// LSP-16333: statuses where Sparky is still working toward a report. CANCELLING is
// deliberately excluded — once the user asks to cancel, the popup must stop
// showing "Running Your Spark...".
const AWAITING_REPORT_STATUSES = new Set<AnalysisStatus>([AnalysisStatus.RUNNING, AnalysisStatus.PENDING, AnalysisStatus.QUEUED]);

const CANCELLABLE_ANALYSIS_STATUSES = new Set<AnalysisStatus>([
	AnalysisStatus.QUEUED,
	AnalysisStatus.PENDING,
	AnalysisStatus.RUNNING,
	AnalysisStatus.WAITING_FOR_INPUT
]);

export function normalizeAnalysisStatus(status: AnalysisStatus | string | null | undefined): AnalysisStatus | undefined {
	if (!status) return undefined;
	if (Object.values(AnalysisStatus).includes(status as AnalysisStatus)) {
		return status as AnalysisStatus;
	}
	const normalized = String(status)
		.trim()
		.replace(/[\s-]+/g, "_")
		.toUpperCase();
	return Object.values(AnalysisStatus).find((value) => value === normalized);
}

export function isAwaitingReport(status: AnalysisStatus | string | null | undefined): boolean {
	const normalizedStatus = normalizeAnalysisStatus(status);
	return !!normalizedStatus && AWAITING_REPORT_STATUSES.has(normalizedStatus);
}

export function isCancellableAnalysisStatus(status: AnalysisStatus | string | null | undefined): boolean {
	const normalizedStatus = normalizeAnalysisStatus(status);
	return !!normalizedStatus && CANCELLABLE_ANALYSIS_STATUSES.has(normalizedStatus);
}

// LSP-20160: label of a disabled Cancel control whose cancel is in flight or acknowledged. Shared by
// the EntityReports table, SparkyReportCard, and LuckyPackPopup so every surface reads the same.
export const CANCELLING_LABEL = "Cancelling…";

export function isCancellingAnalysisStatus(status: AnalysisStatus | string | null | undefined): boolean {
	return normalizeAnalysisStatus(status) === AnalysisStatus.CANCELLING;
}

// LSP-20160: statuses whose row renders a Cancel control at all — the cancellable ones (enabled) plus
// CANCELLING, which keeps the control visible but disabled as "Cancelling…" until Scribe reports a
// terminal status. Terminal rows (completed/failed/cancelled) render no Cancel control.
export function hasCancelControl(status: AnalysisStatus | string | null | undefined): boolean {
	return isCancellableAnalysisStatus(status) || isCancellingAnalysisStatus(status);
}

export function isWaitingForDeferredMedia(status: AnalysisStatus | string | null | undefined, deferredMedia?: boolean): boolean {
	const normalizedStatus = normalizeAnalysisStatus(status);
	return deferredMedia === true && (normalizedStatus === AnalysisStatus.QUEUED || normalizedStatus === AnalysisStatus.PENDING);
}

export function isFetchingReport(status: AnalysisStatus | string | null | undefined, deferredMedia?: boolean): boolean {
	const normalizedStatus = normalizeAnalysisStatus(status);
	if (!normalizedStatus || !AWAITING_REPORT_STATUSES.has(normalizedStatus)) return false;
	return !isWaitingForDeferredMedia(normalizedStatus, deferredMedia);
}
